import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';

import { ApplicationHttpClient } from 'app/components/shared/http/application-http-client';
import { Routes } from '../../config/routes';
import { LegFiJwtService } from '../auth/legfi-jwt.service';
import { GableQuestion } from './gable-stream.service';
import { GableQuestionAnswer } from './gable.service';

export type GableWorkflowStatus = 'active' | 'paused';
export type GableWorkflowRunStatus = 'queued' | 'running' | 'completed' | 'failed';
export type GableWorkflowTriggerType = 'schedule' | 'event';
export type GableWorkflowOutcomeType = 'suggestions' | 'email' | 'document';

export interface GableWorkflowSkill {
    name: string;
    description: string | null;
}

/** Core returns saved recipients with both ids (`membershipId: null` for the requesting user); requests carry exactly one. */
export interface GableWorkflowRecipient {
    membershipId?: number | null;
    userId?: number | null;
    name: string;
    email: string;
}

/** A member goes out by membershipId, the requesting user (or anyone without a membership) by userId. */
export function normalizeRecipient(r: GableWorkflowRecipient): GableWorkflowRecipient {
    const base = { name: r.name, email: r.email };
    if (typeof r.membershipId === 'number' && r.membershipId > 0) {
        return { membershipId: r.membershipId, ...base };
    }
    return { userId: r.userId ?? null, ...base };
}

/** Only what core validates goes over the wire: recipients keyed one way, the document folder as its id. */
export function normalizeOutcomes(outcomes: GableWorkflowOutcome[]): GableWorkflowOutcome[] {
    return outcomes.map((o) => {
        if (o.type === 'email') {
            return { type: 'email', recipients: o.recipients.map(normalizeRecipient) };
        }
        if (o.type === 'document') {
            return { type: 'document', folderId: o.folderId };
        }
        return o;
    });
}

/** One `GET /recipients` row: the requesting user comes first with `isYou`, memberships carry `membershipId`. */
export interface GableWorkflowRecipientOption {
    membershipId: number | null;
    userId?: number | null;
    name: string;
    email: string;
    isYou?: boolean;
}

/** `folderId` -1 is the Documents root; core returns `folderPath` as "Documents / Board Materials / 2026" ("Documents" for the root). */
export type GableWorkflowOutcome =
    | { type: 'suggestions' }
    | { type: 'email'; recipients: GableWorkflowRecipient[] }
    | { type: 'document'; folderId: number; folderPath?: string };

/** Saved filters, e.g. `{form_id: [ids]}`; a missing or empty list means any. */
export type GableWorkflowTriggerFilters = { [key: string]: number[] };

/** Saved params; only `days` exists for now. */
export type GableWorkflowTriggerParams = { days?: number };

export interface GableWorkflowScheduleTrigger {
    type: 'schedule';
    rrule: string;
    cadenceLabel: string;
}

export interface GableWorkflowEventTrigger {
    type: 'event';
    event: string;
    filters: GableWorkflowTriggerFilters;
    params?: GableWorkflowTriggerParams | null;
    cadenceLabel: string;
}

export type GableWorkflowTrigger = GableWorkflowScheduleTrigger | GableWorkflowEventTrigger;

/** Trigger as sent on intake, create and update; core computes the label. */
export type GableWorkflowTriggerInput =
    | Pick<GableWorkflowScheduleTrigger, 'type' | 'rrule'>
    | Pick<GableWorkflowEventTrigger, 'type' | 'event' | 'filters'> & { params?: GableWorkflowTriggerParams };

export interface GableWorkflowTriggerFilterOption {
    value: number;
    label: string;
}

export interface GableWorkflowTriggerFilter {
    key: string;
    label: string;
    required: boolean;
    options: GableWorkflowTriggerFilterOption[];
}

export interface GableWorkflowTriggerParam {
    key: string;
    label: string;
    min: number;
    max: number;
    default: number;
    unit: string;
}

export type GableWorkflowTriggerGroup = 'Requests' | 'Violations' | 'Money' | 'Amenities' | 'Documents' | 'Units';
export type GableWorkflowTriggerKind = 'event' | 'daily';

/** One `GET /triggers` catalog row; the label carries a literal "N" where a `days` param is substituted. */
export interface GableWorkflowTriggerOption {
    key: string;
    label: string;
    labelTemplate?: string;
    description: string;
    available: boolean;
    group: GableWorkflowTriggerGroup;
    kind: GableWorkflowTriggerKind;
    params: GableWorkflowTriggerParam[];
    filter: GableWorkflowTriggerFilter | null;
}

export interface GableWorkflowTriggerEntity {
    type: string;
    id: number;
}

export interface GableWorkflowRun {
    id: number;
    status: GableWorkflowRunStatus;
    triggerLabel?: string | null;
    triggerUrl?: string | null;
    triggerEntity?: GableWorkflowTriggerEntity | null;
    triggerEntityIds?: number[] | null;
    triggerCount?: number | null;
    startedAt: string | null;
    finishedAt: string | null;
    summary: string | null;
    candidatesStaged: number | null;
    emailsSent: number | null;
    documentsSaved?: number | null;
    documentUrl?: string | null;
    documentName?: string | null;
    toolCalls: number | null;
    inputTokens: number | null;
    outputTokens: number | null;
    durationMs: number | null;
    error: string | null;
    createdAt: string | null;
}

export interface GableWorkflowClarifications {
    questions: GableQuestion[];
    answers: GableQuestionAnswer[];
}

export interface GableWorkflow {
    id: number;
    organizationId: number;
    title: string;
    trigger: GableWorkflowTrigger;
    intent: string;
    plan: string;
    summary: string;
    clarifications: GableWorkflowClarifications | null;
    outcomes: GableWorkflowOutcome[];
    skill: GableWorkflowSkill | null;
    status: GableWorkflowStatus;
    nextRunAt: string | null;
    lastRunAt: string | null;
    createdBy: number | null;
    createdAt: string | null;
    updatedAt: string | null;
    runs: GableWorkflowRun[];
}

export interface GableWorkflowInput {
    title: string;
    trigger: GableWorkflowTriggerInput;
    intent: string;
    plan: string;
    summary: string;
    outcomes: GableWorkflowOutcome[];
    skillName?: string | null;
    clarifications?: GableWorkflowClarifications | null;
}

/** PATCH body; `skillName: null` clears the skill; `intent`, `plan` and `summary` travel together after a re-run intake. */
export type GableWorkflowUpdate = Partial<Pick<GableWorkflowInput, 'title' | 'intent' | 'plan' | 'summary' | 'trigger' | 'outcomes' | 'skillName'>>;

export interface GableWorkflowIntakeBody {
    intent: string;
    trigger: GableWorkflowTriggerInput;
    outcomes: GableWorkflowOutcome[];
    skillName?: string;
    draftId?: string;
    questions?: GableQuestion[];
    answers?: GableQuestionAnswer[];
}

export interface GableWorkflowIntakeQuestions {
    phase: 'questions';
    draftId: string;
    understanding: string;
    questions: GableQuestion[];
}

export interface GableWorkflowOutcomeHints {
    emailRequested: boolean;
    recipientsText: string | null;
}

export interface GableWorkflowIntakePlan {
    phase: 'plan';
    draftId: string;
    title: string;
    plan: string;
    summary: string;
    skillName: string | null;
    outcomeHints?: GableWorkflowOutcomeHints;
    cadenceLabel?: string;
    nextRunAt?: string | null;
}

export type GableWorkflowIntakeResponse = GableWorkflowIntakeQuestions | GableWorkflowIntakePlan;

export interface GableWorkflowRunsPage {
    runs: GableWorkflowRun[];
    page: number;
    perPage: number;
    total: number;
}

@Injectable({ providedIn: 'root' })
export class GableWorkflowsService {
    constructor(private readonly _http: ApplicationHttpClient) {}

    list(organizationId?: number): Observable<{ workflows: GableWorkflow[] }> {
        const orgId = this._orgId(organizationId);
        return this._http.get<{ workflows: GableWorkflow[] }>(
            Routes.MakeLegFiCoreUrl(Routes.GableModule.Workflows(orgId)),
        );
    }

    skills(organizationId?: number): Observable<{ skills: GableWorkflowSkill[] }> {
        const orgId = this._orgId(organizationId);
        return this._http.get<{ skills: GableWorkflowSkill[] }>(
            Routes.MakeLegFiCoreUrl(Routes.GableModule.WorkflowsSkills(orgId)),
        );
    }

    recipients(q = '', organizationId?: number): Observable<{ recipients: GableWorkflowRecipientOption[] }> {
        const orgId = this._orgId(organizationId);
        const url = Routes.MakeLegFiCoreUrl(Routes.GableModule.WorkflowsRecipients(orgId));
        return this._http.get<{ recipients: GableWorkflowRecipientOption[] }>(
            q.trim() ? `${url}?q=${encodeURIComponent(q.trim())}` : url,
        );
    }

    triggers(organizationId?: number): Observable<{ triggers: GableWorkflowTriggerOption[] }> {
        const orgId = this._orgId(organizationId);
        return this._http.get<{ triggers: GableWorkflowTriggerOption[] }>(
            Routes.MakeLegFiCoreUrl(Routes.GableModule.WorkflowsTriggers(orgId)),
        );
    }

    requestTrigger(text: string, organizationId?: number): Observable<{ ok: boolean }> {
        const orgId = this._orgId(organizationId);
        return this._http.post<{ ok: boolean }>(
            Routes.MakeLegFiCoreUrl(Routes.GableModule.WorkflowsTriggerRequests(orgId)),
            this._http.serializeObject({ text: text.trim() }),
        );
    }

    runs(id: number, page = 1, organizationId?: number): Observable<GableWorkflowRunsPage> {
        const orgId = this._orgId(organizationId);
        return this._http.get<GableWorkflowRunsPage>(
            `${Routes.MakeLegFiCoreUrl(Routes.GableModule.WorkflowRuns(orgId, id))}?page=${page}`,
        );
    }

    intake(body: GableWorkflowIntakeBody, organizationId?: number): Observable<GableWorkflowIntakeResponse> {
        const orgId = this._orgId(organizationId);
        return this._http.post<GableWorkflowIntakeResponse>(
            Routes.MakeLegFiCoreUrl(Routes.GableModule.WorkflowsIntake(orgId)),
            this._http.serializeObject(this._withNormalizedOutcomes(body)),
        );
    }

    create(body: GableWorkflowInput, organizationId?: number): Observable<{ workflow: GableWorkflow }> {
        const orgId = this._orgId(organizationId);
        return this._http.post<{ workflow: GableWorkflow }>(
            Routes.MakeLegFiCoreUrl(Routes.GableModule.Workflows(orgId)),
            this._http.serializeObject(this._withNormalizedOutcomes(body)),
        );
    }

    update(id: number, body: GableWorkflowUpdate, organizationId?: number): Observable<{ workflow: GableWorkflow }> {
        const orgId = this._orgId(organizationId);
        return this._http.patch<{ workflow: GableWorkflow }>(
            Routes.MakeLegFiCoreUrl(Routes.GableModule.Workflow(orgId, id)),
            this._http.serializeObject(this._withNormalizedOutcomes(body)),
        );
    }

    remove(id: number, organizationId?: number): Observable<{ success: boolean }> {
        const orgId = this._orgId(organizationId);
        return this._http.delete<{ success: boolean }>(
            Routes.MakeLegFiCoreUrl(Routes.GableModule.Workflow(orgId, id)),
        );
    }

    pause(id: number, organizationId?: number): Observable<{ workflow: GableWorkflow }> {
        const orgId = this._orgId(organizationId);
        return this._http.post<{ workflow: GableWorkflow }>(
            Routes.MakeLegFiCoreUrl(Routes.GableModule.WorkflowPause(orgId, id)),
            this._http.serializeObject({}),
        );
    }

    resume(id: number, organizationId?: number): Observable<{ workflow: GableWorkflow }> {
        const orgId = this._orgId(organizationId);
        return this._http.post<{ workflow: GableWorkflow }>(
            Routes.MakeLegFiCoreUrl(Routes.GableModule.WorkflowResume(orgId, id)),
            this._http.serializeObject({}),
        );
    }

    /** Super-only server-side; the panel hides the action for everyone else. */
    runNow(id: number, organizationId?: number): Observable<{ run: GableWorkflowRun; result: unknown }> {
        const orgId = this._orgId(organizationId);
        return this._http.post<{ run: GableWorkflowRun; result: unknown }>(
            Routes.MakeLegFiCoreUrl(Routes.GableModule.WorkflowRunNow(orgId, id)),
            this._http.serializeObject({}),
        );
    }

    private _withNormalizedOutcomes<T extends { outcomes?: GableWorkflowOutcome[] }>(body: T): { [k: string]: any } {
        return body.outcomes ? { ...body, outcomes: normalizeOutcomes(body.outcomes) } : body;
    }

    private _orgId(organizationId?: number): number {
        if (organizationId !== undefined) {
            return organizationId;
        }
        const jwt = LegFiJwtService.read();
        if (jwt === null) {
            throw new Error('No JWT: cannot call the workflows endpoint');
        }
        return jwt.orgId;
    }
}
